import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type InputHTMLAttributes } from "react";
import { cn } from "@/lib/utils";

const inputVariants = cva(
  [
    "w-full text-sands-800 placeholder:text-sands-400",
    "focus:outline-none transition-all duration-200",
    "disabled:opacity-50 disabled:cursor-not-allowed",
  ].join(" "),
  {
    variants: {
      accent: {
        sand: "focus:ring-2 focus:ring-sands-300 focus:border-transparent",
        healing: "focus:ring-2 focus:ring-healing-300 focus:border-transparent",
        none: "",
      },
      surface: {
        default: "bg-white",
        muted: "bg-sands-50",
        ghost: "bg-transparent",
      },
      borderWeight: {
        default: "border-2 border-sands-200",
        thin: "border border-sands-200",
        none: "border-0",
      },
      inputSize: {
        xs: "px-2 py-1 text-xs rounded-lg",
        sm: "px-3 py-2 text-sm rounded-lg",
        default: "px-4 py-3 rounded-lg",
        lg: "px-4 py-3 rounded-xl",
      },
      mono: {
        true: "font-mono",
        false: "",
      },
    },
    defaultVariants: {
      accent: "sand",
      surface: "default",
      borderWeight: "default",
      inputSize: "default",
      mono: false,
    },
  },
);

export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "size">,
    VariantProps<typeof inputVariants> {}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      accent,
      surface,
      borderWeight,
      inputSize,
      mono,
      type = "text",
      ...props
    },
    ref,
  ) => (
    <input
      type={type}
      className={cn(
        inputVariants({ accent, surface, borderWeight, inputSize, mono }),
        className,
      )}
      ref={ref}
      {...props}
    />
  ),
);
Input.displayName = "Input";

export { inputVariants };
